import { useCallback, useMemo } from 'react';
import { useQueries } from '@common-fe/common-fe';
import { FiservCardHolder, FiservCardInterface, FiservCardStatusEnum } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos';
import { GET_CARDS_QUERY_PARAMS_KEYS } from '@/modules/transaction/components/Cards/Cards.constants.ts';

import { CardHoldersResponse, CardsMap, FiservCardPurseDto } from '../components/Cards/Cards.types';
import { useCardFormatting } from '../hooks/useCardFormatting';
import { useQueryGenerator } from '../hooks/useQueryGenerator';

export const QUERY_KEYS = {
  GET_CARDS_HOLDERS: 'GET_CARDS_HOLDERS',
  GET_CARDS_PURSES: 'GET_CARDS_PURSES',
};

export const useGetCardsQuery = (
  employeeId?: number | string | null,
  dependentId?: number | string | null
) => {
  const query = useQueryGenerator(employeeId, dependentId);

  const [cardsQuery, cardPursesQuery] = useQueries({
    queries: [
      {
        queryKey: [QUERY_KEYS.GET_CARDS_HOLDERS, query.toString()],
        queryFn: () =>
          query.get(GET_CARDS_QUERY_PARAMS_KEYS.EMPLOYEE_ID)
            ? api.get<CardHoldersResponse>(API_URL.CARD_HOLDERS + `?${query.toString()}`)
            : null,
      },
      {
        queryKey: [QUERY_KEYS.GET_CARDS_PURSES, employeeId],
        queryFn: () =>
          api.get<FiservCardPurseDto[]>(API_URL.CARD_PURSES, {
            params: { employee_id: employeeId },
          }),
        retry: false,
      },
    ],
  });

  const {
    data: cardPursesData,
    isLoading: isPursesLoading,
    isFetching: isPursesFetching,
    refetch: refetchCardPurses,
  } = cardPursesQuery;
  const { data, isLoading: isCardsLoading, isFetching: isCardsFetching, refetch: refetchCards } = cardsQuery;

  const isLoading = useMemo(() => isCardsLoading || isPursesLoading, [isCardsLoading, isPursesLoading]);
  const isFetching = useMemo(() => isCardsFetching || isPursesFetching, [isCardsFetching, isPursesFetching]);

  const refetch = useCallback(() => {
    refetchCardPurses();
    refetchCards();
  }, [refetchCardPurses, refetchCards]);

  const { getHolderName, formattingCard, formattingCardsResponse, formattingHolder } = useCardFormatting(
    cardPursesData?.data
  );

  const getCardByStatus = useCallback(
    (status: FiservCardStatusEnum) => {
      if (data?.data?.grouped_cards?.[status]) {
        const card = data?.data?.grouped_cards[status][0];
        if (card) {
          return formattingCard(data?.data, card, status);
        }
      }
      return undefined;
    },
    [data?.data, formattingCard]
  );

  const employeePendingOpenCard = useMemo(
    () => getCardByStatus(FiservCardStatusEnum.PENDING_OPEN),
    [getCardByStatus]
  );
  const employeeActiveCard = useMemo(() => getCardByStatus(FiservCardStatusEnum.ACTIVE), [getCardByStatus]);

  const cardMap: CardsMap = useMemo(() => {
    const dependents = data?.data?.dependents || [];
    const rootCardMap: CardsMap = data?.data ? formattingCardsResponse(data?.data) : {};
    const dependentsCardMap: CardsMap = dependents.reduce(
      (map, dependent) =>
        dependent.grouped_cards
          ? {
              ...map,
              ...formattingCardsResponse(dependent),
            }
          : {
              ...map,
            },
      {}
    );

    return {
      ...rootCardMap,
      ...dependentsCardMap,
    };
  }, [data, formattingCardsResponse]);

  const holderList: FiservCardHolder[] = useMemo(
    () => [
      ...(data?.data ? [formattingHolder(data?.data)] : []),
      ...(data?.data.dependents || []).map(formattingHolder),
    ],
    [data, formattingHolder]
  );

  const cardList = useMemo(
    () =>
      Object.keys(cardMap).reduce(
        (cardDtoList, holderName) => [
          ...cardDtoList,
          ...Object.values(cardMap[holderName]).reduce(
            (arr, status) => [...arr, ...status],
            [] as FiservCardInterface[]
          ),
        ],
        [] as FiservCardInterface[]
      ),
    [cardMap]
  );

  const cardAccountId = useMemo(() => cardList[0]?.cardAccountId || null, [cardList]);
  const holderName = getHolderName(data?.data.first_name, data?.data.last_name) || '';
  const holderKey = `${holderName}-${data?.data.id}`;

  return {
    cardMap,
    cardList,
    employeePendingOpenCard,
    employeeActiveCard,
    cardAccountId,
    response: data?.data,
    holderName,
    holderKey,
    holderList,
    refetch,
    isLoading,
    isFetching,
    formattingCard,
    getCardByStatus,
  };
};
